import { useEffect, useState } from 'react';
import { profileService } from 'services/profileService';
import useAPIError from './useThrowAsyncError';
import { useKeycloak } from 'keycloak-react-web';

export type UserProfile = {
    id: string;
    profileImage: {
        base64: string;
        fileType: string;
    };
    name: string;
    username: string;
    email: string;
    phone: string;
    /** Partner del usuario (p. ej. filtros en cascada cuando no hay listado de partners en UI). */
    vendor?: {
        id: string;
        name: string;
    };
    company: {
        code: string;
        id: string;
        name: string;
    };
    dni: string;
    permissions: string[];
    usageLimit?: {
        signatures?: {
            active?: boolean;
            count?: number;
            total?: number;
        };
        dates?: { date: string }[];
    };
};

export type UserProfileUpdated = {
    profileImage: {
        base64: string;
        fileType: string;
    } | null;
    name: string;
    email: string;
    phone: string;
    dni: string;
    oldPwd: string;
};

export type UpdatedPassword = {
    name: string;
    email: string;
    phone: string;
    dni: string;
    oldPwd: string;
    newPwd: string;
};

export default function useProfile() {
    const { addError } = useAPIError();

    const [profile, setProfile] = useState<UserProfile>({
        id: '',
        profileImage: {
            base64: '',
            fileType: ''
        },
        name: '',
        username: '',
        email: '',
        phone: '',
        company: {
            code: '',
            id: '',
            name: ''
        },
        dni: '',
        permissions: []
    });
    const { keycloak } = useKeycloak();
    const [profileIsReady, setProfileIsReady] = useState<Boolean>(false);
    const [blocked, setBlocked] = useState<boolean>(false);

    useEffect(() => {
        keycloak.authenticated && getUserProfile();
    }, [keycloak.authenticated]);

    const getUserProfile = async (): Promise<void> => {
        const userProfile: UserProfile | void = await profileService.getProfileData().catch((err: any) => {
            if (err.code === 1101) {
                setBlocked(true);
                return;
            } else {
                throw addError(err.message, err.details);
            }
        });
        if (!userProfile) {
            setProfileIsReady(true);
            return;
        }
        setProfile(userProfile);
        setProfileIsReady(true);
    };

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const updateUserProfile = async (profile: UserProfileUpdated): Promise<void> => {
        const updatedProfile = await profileService.updateProfileData(profile).catch((err: any) => {
            throw addError(err.message, err.details);
        });
        setProfile(updatedProfile);
    };

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const updateUserPassword = async (profile: UpdatedPassword) => {
        const updatedProfile = await profileService.updateProfileData(profile).catch((err: any) => {
            throw addError(err.message, err.details);
        });
        setProfile(updatedProfile);
    };

    const updateUserSmsConfig = async (id: string, body: any) => {
        await profileService.updateSmsConfig(id, body).catch((err: any) => {
            throw addError(err.message, err.details);
        });
    };

    const updateUserEmailConfig = async (id: string, body: any) => {
        await profileService.updateEmailConfig(id, body).catch((err: any) => {
            throw addError(err.message, err.details);
        });
    };

    const getListFilters = (permission: string) => {
        return profile.permissions.some((p: string) => p === permission);
    };

    return {
        blocked,
        profile,
        updateUserProfile,
        getUserProfile,
        updateUserPassword,
        profileIsReady,
        getListFilters,
        updateUserSmsConfig,
        updateUserEmailConfig
    };
}
